import { useEffect, useState } from 'react';
import { createSecureVault, isPlatformAuthenticatorAvailable } from '../../dry/secure';
import { REFRESH_FAILED } from '../../hooks/useOfflineUnlock';

interface Props {
  offline: boolean;
  onUnlockBiometric: () => Promise<void>;
  onUnlockPin: (pin: string) => Promise<void>;
  onSignInAgain: () => void;
}

export function LockScreen({ offline, onUnlockBiometric, onUnlockPin, onSignInAgain }: Props) {
  const method = createSecureVault().enrolledMethod();
  const [pin, setPin] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [bioAvailable, setBioAvailable] = useState(false);

  useEffect(() => {
    if (method === 'prf') isPlatformAuthenticatorAvailable().then(setBioAvailable);
  }, [method]);

  // Distinguish "the unlock secret was valid but the server rejected the refresh
  // token" (session genuinely expired — must re-sign-in) from a wrong PIN /
  // cancelled biometric. The former points the user at the escape button.
  function messageFor(e: unknown): string | null {
    const msg = e instanceof Error ? e.message : String(e);
    if (msg === REFRESH_FAILED) {
      return 'Your session expired — please sign in again.';
    }
    // The vault throws 'Vault locked: too many PIN attempts' on lockout (it does
    // NOT contain the word "wiped"); also catch the post-wipe not-enrolled state.
    if (msg.includes('Vault locked') || msg.includes('too many') || !createSecureVault().isEnrolled()) {
      return 'Too many attempts — please sign in again.';
    }
    return null;
  }

  async function tryBiometric() {
    setBusy(true); setError(null);
    try { await onUnlockBiometric(); }
    catch (e) { setError(messageFor(e) ?? 'Unlock failed. Try again.'); }
    finally { setBusy(false); }
  }

  async function tryPin() {
    setBusy(true); setError(null);
    try { await onUnlockPin(pin); }
    catch (e) {
      setError(messageFor(e) ?? 'Incorrect PIN.');
      setPin('');
    } finally { setBusy(false); }
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-6 bg-gray-50">
      <h1 className="text-xl font-semibold mb-1">Unlock</h1>
      {offline && <p className="text-xs text-amber-600 mb-3">Offline — you can keep capturing inspections.</p>}
      {error && <p className="text-sm text-red-600 mb-3">{error}</p>}

      {method === 'prf' && bioAvailable && (
        <button disabled={busy} onClick={tryBiometric}
          className="rounded-lg bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 mb-4 transition-colors disabled:opacity-50">
          Unlock with biometrics
        </button>
      )}

      {(method === 'pin' || !bioAvailable) && (
        <div className="w-full max-w-xs space-y-3">
          <input type="password" inputMode="numeric" placeholder="Enter PIN" value={pin}
            onChange={(e) => setPin(e.target.value)}
            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-center" />
          <button disabled={busy || pin.length < 4} onClick={tryPin}
            className="w-full rounded-lg bg-blue-600 hover:bg-blue-700 text-white py-2 transition-colors disabled:opacity-50">Unlock</button>
        </div>
      )}

      {/* Always-visible escape: abandon the stale offline session and re-sign-in
          with a password. The only way out when the 30-day refresh token has
          itself expired (online unlock decrypts fine but /auth/refresh 401s). */}
      <button disabled={busy} onClick={onSignInAgain}
        className="mt-6 text-sm text-gray-600 underline hover:text-gray-800 disabled:opacity-50">
        Sign in with password
      </button>
    </div>
  );
}
